import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Transition from '../utils/Transition';
import { useUser } from '../userContext'; // Import the useUser hook
import UserAvatar from '../images/profiledefault.png';

// Updated component - should show SETTINGS and SIGN OUT
function DropdownProfile({ align, handleSignOut }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Get user and business data from the context
  const { businessProfile, userProfile, isOwner } = useUser();
  const businessName = businessProfile?.name || '';
  const userName = userProfile?.firstName || 'Please update your profile details';

  // Get the first initial from the user's name for fallback avatar
  const userInitial = userName.charAt(0).toUpperCase();

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (!dropdownOpen || dropdown.current.contains(target) || trigger.current.contains(target)) return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // Close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  return (
    <div className="relative inline-flex">
      <button
        ref={trigger}
        className="inline-flex justify-center items-center group p-2 rounded-lg transition-all duration-100"
        style={{
          background: 'rgba(52, 73, 94, 0.6)',
          fontFamily: '"Share Tech Mono", monospace'
        }}
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        {businessProfile?.headlinePhoto ? (
          <img
            className="w-8 h-8 rounded-full"
            src={businessProfile.headlinePhoto}
            width="32"
            height="32"
            alt="User"
          />
        ) : (
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
            style={{
              background: 'linear-gradient(135deg, #F1C40F, #F39C12)',
              color: '#2C3E50'
            }}
          >
            {userInitial}
          </div>
        )}
        <div className="flex items-center truncate">
          <span className="truncate ml-2 text-sm font-bold tracking-wide"
            style={{
              color: '#F1C40F',
              textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
            }}
          >
            {businessName}
          </span>
        </div>
      </button>

      <Transition
        className={`origin-top-right z-[9999] absolute top-full min-w-44 py-1.5 rounded-lg shadow-lg overflow-hidden mt-1 ${align === 'right' ? 'right-0' : 'left-0'}`}
        style={{
          background: '#1A252F',
          border: '2px solid #F1C40F',
          fontFamily: '"Share Tech Mono", monospace',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.9)'
        }}
        show={dropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          className="pb-4 rounded-lg"
          style={{background: '#1A252F'}}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          <div className="pt-0.5 pb-2 px-3 mb-1 border-b-2" style={{
            borderColor: 'rgba(241, 196, 15, 0.3)'
          }}>
            <div className="font-bold tracking-wide" style={{
              color: '#F1C40F',
              textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
            }}>{businessName}</div>
            <div className="text-xs italic font-medium" style={{
              color: '#BDC3C7'
            }}>
              {isOwner ? '👑 ADMIN: ' : '👤 USER: '}
              {userName}
            </div>
          </div>
          <ul>
            <li>
              <Link
                className="font-bold text-sm flex items-center py-2 px-3 transition-all duration-100"
                style={{
                  color: '#ECF0F1',
                  fontFamily: '"Share Tech Mono", monospace'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(241, 196, 15, 0.1)';
                  e.target.style.color = '#F1C40F';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#ECF0F1';
                }}
                to="/studio/settings/account"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                ⚙️ SETTINGS
              </Link>
            </li>
            <li>
              <button
                className="font-bold text-sm flex items-center py-2 px-3 transition-all duration-100"
                style={{
                  color: '#ECF0F1',
                  fontFamily: '"Share Tech Mono", monospace'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(231, 76, 60, 0.1)';
                  e.target.style.color = '#E74C3C';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#ECF0F1';
                }}
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  handleSignOut();
                }}
              >
                🚪 SIGN OUT
              </button>
            </li>
          </ul>
        </div>
      </Transition>
    </div>
  );
}

export default DropdownProfile;